import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';

import CONSTANTS from '../../_constants/constants';

import { SharedModule } from '../../_shared/shared.module';
import { AlertService } from '../../_shared/services/alert.service';
import { UtilService } from '../../_shared/services/util.service';
import { GoogleMapService } from '../../_shared/services/google-map.service';

import { PickupPlace } from '../shared/pickup-place.model';
import { PickupPlaceModalService } from '../shared/pickup-place-modal.service';
import { PickupPlaceService } from '../shared/pickup-place.service';

import { LocationService } from '../../locations/shared/location.service';
import { LocationModalService } from '../../locations/shared/location-modal.service';
import { ZoneService } from '../../zones/shared/zone.service';
import { ZoneModalService } from '../../zones/shared/zone-modal.service';

@Component({
  selector: 'app-pickup-place-detail',
  templateUrl: './pickup-place-detail.component.html',
  standalone: true,
  imports: [SharedModule],
})
export class PickupPlaceDetailComponent implements OnInit {
  @Input() pickupPlace: PickupPlace;
  @Input() mode: string;
  @Input() defaultLanguage;
  @Input() additionalLanguages;
  @Input() isModal = false;

  @Output() private savedPickupPlace = new EventEmitter<PickupPlace>();
  @Output() private canceledPickupPlace = new EventEmitter();

  form: FormGroup;
  formSubmited = false;

  createMode = false;
  editMode = false;
  viewMode = false;

  isHotel = false;
  pickupPlaceOptions = CONSTANTS.PICKUP_PLACES_OPTIONS;
  labelAddLogo = '';

  loadingLocations = false;
  loadingZones = false;

  locations = [];
  zones = [];

  serverErrors = [];
  sendingForm = false;

  private pickupPlaceCopy = null;
  private photo = null;

  googleMapLoaded = false;

  constructor(
    private pickupPlaceService: PickupPlaceService,
    private pickupPlaceModalService: PickupPlaceModalService,
    private locationService: LocationService,
    private locationModalService: LocationModalService,
    private zoneService: ZoneService,
    private zoneModalService: ZoneModalService,
    private formBuilder: FormBuilder,
    private alertService: AlertService,
    private utilService: UtilService,
    private googleMapService: GoogleMapService
  ) {
    this.googleMapService.loadGoogleMapScript().then(() => {
      this.googleMapLoaded = true;
    });
  }

  ngOnInit() {
    this.setPickupPlaceForm();

    if (this.pickupPlace.id) {
      this.setItemOptions(this.pickupPlace, this.mode);

      this.form.patchValue(this.pickupPlace);
    } else {
      this.createMode = true;
    }

    this.getLocations();
  }

  // Referenced from app-translation-options
  onOpenedTranslation(language: any) {
    this.pickupPlaceModalService.openPickupPlaceTranslation(this.pickupPlace, language);
  }

  openLocationsModal() {
    const successCallback = () => {
      this.getLocations();
    };

    this.locationModalService.openLocationsModal(successCallback);
  }

  selectLocation(locationId) {
    this.form.controls['zone_id'].setValue(null);
    this.getZonesByLocation(locationId);
  }

  openZonesModal() {
    const successCallback = () => {
      this.getZonesByLocation(this.pickupPlace.location_id);
    };

    this.zoneModalService.openZonesModal(successCallback);
  }

  openLogo() {
    const successCallback = ({ file, croppedImage }) => {
      // console.log('file: ', file);
      // console.log('croppedImage: ', croppedImage);

      if (this.editMode) {
        this.pickupPlaceService
          .updatePickupLogo(this.pickupPlace.id, file)
          .subscribe((response) => {
            this.alertService.open('PHOTO_UPDATED');

            // this.pickupPlace.logo = response.logo;
            this.pickupPlace.logoUrl = this.utilService.cleanItemCache(response, 'logo');
          });
      } else {
        this.photo = file;

        this.pickupPlace.logoUrl = croppedImage;
      }
    };

    this.utilService.openLogo(successCallback);
  }

  // Referenced from app-map
  onSelectedMapPlace({ lat, lng, zoom }) {
    this.form.controls['geo_lat'].setValue(lat);
    this.form.controls['geo_lng'].setValue(lng);
    this.form.controls['geo_zoom'].setValue(zoom);
  }

  // Referenced from app-item-detail-form-actions
  onSavedPickupPlace() {
    this.formSubmited = true;

    if (this.form.invalid || this.locations.length === 0 || this.zones.length === 0) {
      this.serverErrors = [];

      return;
    }

    const formData = this.form.value;

    const fields = [
      'name',
      'address',
      'type',
      'location_id',
      'zone_id',
      'geo_lat',
      'geo_lng',
      'geo_zoom',
    ];

    if (this.editMode && !fields.some((field) => formData[field] !== this.pickupPlaceCopy[field])) {
      this.onCanceled();
      return;
    }

    this.sendingForm = true;

    // this.pickupPlace = this.form.value; // not passing the id
    // console.log(this.pickupPlace);
    // console.log(formData);

    this.pickupPlaceService
      .savePickupPlace(this.pickupPlace.id, formData, this.defaultLanguage.lang, this.photo)
      .subscribe({
        next: (response) => {
          let msg = '';

          if (this.editMode) {
            msg = 'PICKUP_PLACE_UPDATED';

            // needed if we want to keep the page opened or if used in page
            this.pickupPlace = response;
            this.setItemOptions(response);
          } else {
            msg = 'PICKUP_PLACE_CREATED';
            // this.pickupPlace = response; // reload item - needed ?

            // if (!this.isModal) {
            // this.savedPickupPlace.emit(response);
            // }
          }

          this.alertService.open(msg);

          this.sendingForm = false;

          // if (this.isModal) {
          this.savedPickupPlace.emit(response);
          // }
        },
        error: (response) => {
          this.serverErrors = this.utilService.setFormServerErrors(response.error.errors);

          this.utilService.scrollToTop();

          this.sendingForm = false;
        },
      });
  }

  // Referenced from app-item-detail-form-actions
  onCanceled() {
    if (this.editMode) {
      this.cancelEdit();
      return;
    }

    this.canceledPickupPlace.emit();
  }

  // Referenced from app-item-detail-form-actions
  onEditedPickupPlace() {
    this.editMode = true;
    this.viewMode = false;
  }

  private cancelEdit() {
    this.pickupPlace = this.pickupPlaceCopy;

    this.setItemOptions(this.pickupPlace);
  }

  private setItemOptions(pickupPlace, mode = '') {
    this.createMode = false;
    this.editMode = false;
    this.viewMode = true;

    if (mode === 'edit') {
      this.onEditedPickupPlace();
    }

    if (this.pickupPlace && pickupPlace.type_id) {
      // Is hotel
      this.editMode = false;
      this.isHotel = true;
    }

    if (pickupPlace.location_id) {
      this.getZonesByLocation(pickupPlace.location_id);
    }

    this.labelAddLogo =
      this.pickupPlace && this.pickupPlace.logo.url ? 'CHANGE_PHOTO' : 'ADD_PHOTO';

    this.pickupPlaceCopy = { ...pickupPlace };
  }

  private setPickupPlaceForm() {
    this.form = this.formBuilder.group({
      name: [
        '',
        {
          validators: [
            Validators.required,
            // Validators.pattern(this.utilService.selectInputPattern('text')),
          ],
          updateOn: 'blur',
        },
      ],
      address: '',
      type: ['', Validators.required],
      location_id: [null, Validators.required], //''
      zone_id: ['', Validators.required],
      geo_lat: '',
      geo_lng: '',
      geo_zoom: '',
    });
  }

  private getLocations() {
    const params = {
      get: 'active',
      skip_check_role: true,
      lang: this.defaultLanguage.lang,
    };

    this.loadingLocations = true;

    this.locationService.getLocations(params).subscribe((response) => {
      const locations = [...response];

      this.locations = this.locationService.groupLocations(locations); // , '', 'locations'

      this.loadingLocations = false;
    });
  }

  private getZonesByLocation(locationId) {
    const params = {
      get: 'by_location',
      location_id: locationId,
      skip_check_role: true,
      lang: this.defaultLanguage.lang,
    };

    this.loadingZones = true;

    this.zoneService.getZones(params).subscribe((response) => {
      this.zones = response;

      this.loadingZones = false;
    });
  }
}

/*

this.form = this.formBuilder.group({
      name: [
        this.pickupPlace.name,
        {
          validators: [
            Validators.required,
            // Validators.pattern(this.utilService.selectInputPattern('text')),
          ],
          updateOn: 'blur',
        },
      ],
      address: [this.pickupPlace.address],
      type: [this.pickupPlace.type, Validators.required],
      location_id: [this.pickupPlace.location_id, Validators.required],
      zone_id: [this.pickupPlace.zone_id, Validators.required],
      geo_lat: [this.pickupPlace.geo_lat],
      geo_lng: [this.pickupPlace.geo_lng],
      geo_zoom: [this.pickupPlace.geo_zoom], // , Validators.required
    });

const formData = this.utilService.deleteNullFields(fields, this.form.value);

*/
